import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { Currency } from "./manufacturing";

/**
 * Purchasing: orders to suppliers, and receiving them into stock.
 *
 * A receipt is a stock document and its lines are ledger movements, so there
 * is no receipt resource here — the order's receipts are read from the stock
 * ledger, filtered by order. Wire names match the Java `@JsonProperty`s.
 */

/**
 * AWAITING_APPROVAL: issued at or above the purchasing settings' threshold, so
 * it waits for the approver before the supplier sees it. Its lines are frozen
 * until it is approved or sent back.
 */
export const PURCHASE_ORDER_STATUSES = ["DRAFT", "AWAITING_APPROVAL", "ISSUED", "CLOSED", "CANCELLED"] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

/** MANUAL: written by a person. REPLENISHMENT: drafted from the Replenishment screen. AUTOMATIC: prepared by the nightly run, untouched since. */
export const PURCHASE_ORDER_ORIGINS = ["MANUAL", "REPLENISHMENT", "AUTOMATIC"] as const;
export type PurchaseOrderOrigin = (typeof PURCHASE_ORDER_ORIGINS)[number];

export const RECEIPT_PROGRESSES = ["NONE", "PARTIAL", "COMPLETE"] as const;
export type ReceiptProgress = (typeof RECEIPT_PROGRESSES)[number];

/**
 * ITEM is a part, received into stock. EXPENSE is spending that never enters
 * stock — maintenance, courier, fuel, rental; SIGM's part DIV — and is
 * delivered instead. TEXT is words for the supplier.
 */
export type PurchaseOrderLineKind = "ITEM" | "EXPENSE" | "TEXT";

export interface PurchaseOrderDTO {
  id: number;
  /** Assigned by the database; nexpim numbers from 100001, imported orders keep SIGM's. */
  number: number;
  supplier_id: number;
  supplier_code?: string | null;
  supplier_name?: string | null;
  warehouse_id: number;
  warehouse_code?: string | null;
  /** Changed only through the order's actions, never by saving the header. */
  status: PurchaseOrderStatus;
  /** Computed from receipts. */
  receipt_progress?: ReceiptProgress | null;
  order_date: string | null;
  required_date: string | null;
  currency: Currency;
  supplier_reference: string | null;
  shipping_cost: number;
  note: string | null;
  subtotal?: number | null;
  line_count: number;
  open_line_count: number;
  last_receipt_date?: string | null;
  created_by_account_id?: number | null;
  issued_at?: string | null;
  closed_at?: string | null;
  sigm_purchase_order_id?: number | null;
  origin?: PurchaseOrderOrigin;
  /** Issued, but the supplier is not to ship until it is released — waiting for a full truck. */
  is_on_hold?: boolean;
  /** The day the supplier was told to ship; lead time counts from here. */
  released_date?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  approved_by_account_id?: number | null;
  /** The approver's word: why it was sent back, or a remark on approval. */
  approval_note?: string | null;
  /** Subtotal plus shipping, in CAD. */
  total_cad?: number | null;
}

export interface PurchaseOrderLineDTO {
  id: number;
  purchase_order_id: number;
  position: number;
  kind: PurchaseOrderLineKind;
  part_id: number | null;
  part_number?: string | null;
  part_name?: string | null;
  supplier_part_number: string | null;
  description: string | null;
  /** In unit_id — the supplier's unit. */
  quantity: number;
  /** Null means the part's stock unit. */
  unit_id: number | null;
  /** Stock units in one ordered unit: a box of 12 is 12. */
  stock_units_per_unit: number;
  /** The PER_PART order unit the quantity is in (2 barrels). Null: unit_id, or the stock unit. */
  order_unit_id?: number | null;
  /** Read only. */
  order_unit_code?: string | null;
  unit_price: number;
  required_date: string | null;
  /** The date the supplier committed to. Planning and the cash projection trust it over the required date. */
  promised_date?: string | null;
  quantity_cancelled: number;
  /** EXPENSE lines only: how much has been delivered. Never in stock. */
  quantity_delivered: number;
  /** Computed — from the ledger for items, from deliveries for expenses — in ordered units. */
  quantity_received?: number | null;
  quantity_outstanding?: number | null;
  note: string | null;
  /** Read only: the order the line is on, for a part's purchase history across orders. */
  order_number?: number | null;
  order_status?: PurchaseOrderStatus | null;
  order_date?: string | null;
  currency?: string | null;
  supplier_id?: number | null;
  supplier_code?: string | null;
  supplier_name?: string | null;
}

export interface ReceiptRequest {
  purchase_order_id: number | null;
  supplier_id: number | null;
  warehouse_id: number | null;
  document_date: string | null;
  /** The supplier's packing slip number. */
  reference: string | null;
  note: string | null;
  lines: Array<{
    purchase_order_line_id: number | null;
    part_id: number | null;
    quantity: number;
    unit_price: number | null;
    note: string | null;
  }>;
}

export interface ReceiptResult {
  stock_document_id: number;
  lines: Array<{
    part_number: string;
    quantity: number;
    stock_quantity: number;
    outstanding_after: number | null;
    over_received: boolean;
    /** Null for an expense line, which touches no stock. */
    stock_movement_id: number | null;
  }>;
  /** What a subcontractor used up at its own warehouse to make what was received. */
  consumed?: Array<{
    part_number: string;
    warehouse_code: string;
    quantity: number;
    for_part_number: string;
    stock_movement_id: number;
  }>;
}

export class PurchaseOrderClient extends ResourceClient<PurchaseOrderDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/purchase-order");
  }

  /** To the supplier — or to approval, at or above the threshold. On hold: the supplier has it but waits to ship. */
  issue(id: number, onHold = false): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/issue`, { on_hold: onHold });
  }

  approve(id: number, note: string | null = null): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/approve`, { note });
  }

  /** Back to draft; the note says what to change and is required. */
  sendBack(id: number, note: string): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/send-back`, { note });
  }

  release(id: number): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/release`, {});
  }

  hold(id: number): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/hold`, {});
  }

  async canApprove(): Promise<boolean> {
    const result = await this.http.get<{ can_approve: boolean }>(`${this.path}/can-approve`);
    return result.can_approve;
  }

  close(id: number): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/close`, {});
  }

  cancel(id: number): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/cancel`, {});
  }

  reopen(id: number): Promise<PurchaseOrderDTO> {
    return this.http.post<PurchaseOrderDTO>(`${this.path}/${String(id)}/reopen`, {});
  }
}

export class PurchaseOrderLineClient extends ResourceClient<PurchaseOrderLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/purchase-order-line");
  }
}

export class PurchasingClient {
  constructor(private readonly http: HttpClient) {}

  receive(request: ReceiptRequest): Promise<ReceiptResult> {
    return this.http.post<ReceiptResult>("/purchasing/receipt", request);
  }
}

/** The line total, in the order's currency. */
export function lineTotal(line: Pick<PurchaseOrderLineDTO, "quantity" | "unit_price" | "kind">): number {
  return line.kind === "TEXT" ? 0 : Number(line.quantity) * Number(line.unit_price);
}
